import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import 'survey-core/defaultV2.min.css';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import { useSelector } from 'react-redux';
import { Modal, Button, Table, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import useSurveyTemplate from '../../hooks/useSurveyTemplate';
import useContenutiUtenti from '../../hooks/useContenutiUtenti';//NEW
import { registerSurveyRipassoProperties } from '../../survey/registerSurveyRipassoProperties';

registerSurveyRipassoProperties();

/** Superamento quiz: coincide con il punteggio % (domande peso 1 ⇒ es. 6/10, 18/30). */
const QUIZ_PASS_THRESHOLD_PERCENT = 60;

function QuizExecutionPage() {
    const [showResults, setShowResults] = useState(false);
    const [quizResults, setQuizResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [surveyData, setSurveyData] = useState(null);
    const [countdown, setCountdown] = useState(3);
    const [showQuiz, setShowQuiz] = useState(false);
    const user = useSelector(state => state.user?.userData);
    const location = useLocation();
    const navigate = useNavigate();
    const { classeData, selectedContenuto } = location.state || {};
    const { getSurveyTemplateByIdContenuto } = useSurveyTemplate();
    const { salvaTest } = useContenutiUtenti();

    /** Deep clone: Survey `Model` muta l'oggetto JSON e può rimuovere proprietà custom (es. `ripasso` sulle choices). */
    const cloneSurveyJson = (obj) => JSON.parse(JSON.stringify(obj));

    /** Copia immutabile del template solo per il ripasso (mai passata a `Model`). */
    const ripassoDefinitionRef = useRef(null);

    const parseJsonDefinition = (raw) => {
        if (raw == null) {
            return null;
        }
        let def = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (typeof def === 'string') {
            try {
                def = JSON.parse(def);
            } catch {
                return null;
            }
        }
        return def;
    };

    useEffect(() => {
        loadSurveyTemplate();
    }, []);

    useEffect(() => {
        if (surveyData && countdown > 0) {
            const timer = setInterval(() => {
                setCountdown(prev => prev - 1);
            }, 1000);

            return () => clearInterval(timer);
        }
        
        if (countdown === 0) {
            setShowQuiz(true);
        }
    }, [countdown, surveyData]);

    const loadSurveyTemplate = async () => {
        try {
            const template = await getSurveyTemplateByIdContenuto({ id_contenuto: selectedContenuto.id_contenuto });
            if (template?.json_definition) {
                const def = parseJsonDefinition(template.json_definition);
                if (def) {
                    ripassoDefinitionRef.current = cloneSurveyJson(def);
                    setSurveyData(def);
                } else {
                    ripassoDefinitionRef.current = null;
                    setSurveyData(null);
                }
            } else {
                ripassoDefinitionRef.current = null;
                setSurveyData(null);
            }
        } catch (error) {
            console.error('Errore nel caricamento del template:', error);
            ripassoDefinitionRef.current = null;
        } finally {
            setLoading(false);
        }
    };

    const alertResults = useCallback((sender) => {
        let totalScore = 0;
        let maxPossibleScore = 0;

        const choiceDisplayText = (question, value) => {
            if (value === undefined || value === null || value === '') {
                return 'Non risposto';
            }
            const choices = question.choices;
            if (!choices?.length) {
                return String(value);
            }
            const item = choices.find((c) =>
                typeof c === 'object' && c !== null ? c.value === value : c === value
            );
            if (item == null) {
                return String(value);
            }
            if (typeof item === 'object' && item !== null) {
                return item.text != null && item.text !== ''
                    ? item.text
                    : String(item.value);
            }
            return String(item);
        };

        const choiceValueEquals = (c, value) => {
            if (c == null || value === undefined || value === null || value === '') {
                return false;
            }
            if (typeof c === 'object') {
                const v = c.value;
                return v === value || String(v) === String(value);
            }
            return c === value || String(c) === String(value);
        };

        /**
         * Ripasso dal JSON del template (stato React), mai dall'oggetto passato a `Model` (viene mutato).
         * Supporta: `choices[].ripasso`, mappa opzionale `ripassoMap` sulla domanda, match per `value` o testo risposta.
         */
        const ripassoFromSurveyDefinition = (questionName, value, answerLabel) => {
            const def = ripassoDefinitionRef.current;
            if (!def || value === undefined || value === null || value === '') {
                return null;
            }
            const hasTree =
                (Array.isArray(def.pages) && def.pages.length > 0) ||
                (Array.isArray(def.elements) && def.elements.length > 0);
            if (!hasTree) {
                return null;
            }
            const nameKeys = new Set(
                [questionName, String(questionName).includes('.') ? String(questionName).split('.').pop() : null]
                    .filter(Boolean)
                    .map(String)
            );
            const choiceMatchesAnswer = (c) => {
                if (choiceValueEquals(c, value)) {
                    return true;
                }
                if (answerLabel != null && String(answerLabel).trim() !== '') {
                    const label = String(answerLabel).trim();
                    if (typeof c === 'object' && c != null && c.text != null) {
                        return String(c.text).trim() === label;
                    }
                    if (typeof c === 'string') {
                        return c.trim() === label;
                    }
                }
                return false;
            };
            const tryElement = (el) => {
                if (!el || !nameKeys.has(String(el.name))) {
                    return null;
                }
                if (Array.isArray(el.choices)) {
                    const raw = el.choices.find((c) => choiceMatchesAnswer(c));
                    if (raw && typeof raw === 'object' && raw.ripasso != null && String(raw.ripasso).trim() !== '') {
                        return raw.ripasso;
                    }
                }
                if (el.ripassoMap && typeof el.ripassoMap === 'object') {
                    const mapped =
                        el.ripassoMap[value] ?? el.ripassoMap[String(value)];
                    if (mapped != null && String(mapped).trim() !== '') {
                        return mapped;
                    }
                }
                return null;
            };
            const walkElements = (elements) => {
                if (!Array.isArray(elements)) {
                    return null;
                }
                for (const el of elements) {
                    const hit = tryElement(el);
                    if (hit) {
                        return hit;
                    }
                    if (el?.elements?.length) {
                        const nested = walkElements(el.elements);
                        if (nested) {
                            return nested;
                        }
                    }
                }
                return null;
            };
            if (Array.isArray(def.pages)) {
                for (const page of def.pages) {
                    const r = walkElements(page.elements);
                    if (r) {
                        return r;
                    }
                }
            }
            if (Array.isArray(def.elements)) {
                return walkElements(def.elements);
            }
            return null;
        };

        const detailedResults = sender.getAllQuestions().map((question) => {
            const questionScore = question.score || 1;
            maxPossibleScore += questionScore;

            const selected = sender.data[question.name];
            const isCorrect = question.correctAnswer === selected;
            if (isCorrect) {
                totalScore += questionScore;
            }

            const choice = question.choices?.find(
                (c) => (typeof c === 'object' && c !== null ? c.value : c) === selected
            );
            const userAnswerLabel = choiceDisplayText(question, selected);
            const ripassoFromModel =
                !isCorrect &&
                choice &&
                typeof choice === 'object' &&
                choice.ripasso != null &&
                choice.ripasso !== ''
                    ? choice.ripasso
                    : null;
            const ripasso =
                ripassoFromModel ??
                (!isCorrect
                    ? ripassoFromSurveyDefinition(question.name, selected, userAnswerLabel)
                    : null);

            return {
                title: question.title,
                userAnswer: selected ?? 'Non risposto',
                userAnswerLabel,
                correctAnswer: question.correctAnswer,
                correctAnswerLabel: choiceDisplayText(question, question.correctAnswer),
                isCorrect,
                score: questionScore,
                ripasso
            };
        });

        const timeInSeconds = Math.round(sender.timeSpent || 0);
        const timeInMinutes = Math.ceil(timeInSeconds / 60);

        setQuizResults({
            summary: {
                totalQuestions: sender.getAllQuestions().length,
                correctAnswers: detailedResults.filter(r => r.isCorrect).length,
                score: totalScore,
                maxScore: maxPossibleScore,
                timeSpent: timeInMinutes,
                percentage: maxPossibleScore > 0 ? Math.round((totalScore / maxPossibleScore) * 100) : 0
            },
            details: detailedResults
        });

        setShowResults(true);
    }, [user]);

    const hasSavedTest = useRef(false);
    useEffect(() => {
        const passed =
            (quizResults?.summary?.maxScore ?? 0) > 0 &&
            (quizResults?.summary?.percentage ?? 0) >= QUIZ_PASS_THRESHOLD_PERCENT;
        if (passed && !hasSavedTest.current && selectedContenuto?.id_contenuto) {
            hasSavedTest.current = true;
            (async () => {
                try {
                    await salvaTest({ id_contenuto: selectedContenuto.id_contenuto, test_data: quizResults });
                } catch (error) {
                    console.error('Errore nel salvataggio del test:', error);
                    hasSavedTest.current = false;
                } finally {
                    setLoading(false);
                }
            })();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- salvaTest dalla hook non è stabilizzata con useCallback
    }, [quizResults, selectedContenuto?.id_contenuto]);

    const survey = useMemo(() => {
        if (!surveyData) return null;
        const m = new Model(cloneSurveyJson(surveyData));
        m.onComplete.add(alertResults);
        m.showCorrectAnswers = true;
        m.showCompletedPage = true;
        m.completedHtml = 'Grazie per aver completato il test!';
        m.showProgressBar = surveyData.showProgressBar ?? 'bottom';
        m.showTimerPanel = surveyData.showTimerPanel ?? 'top';
        m.startSurveyText = surveyData.startSurveyText ?? 'Inizia il Quiz';
        m.maxTimeToFinish = surveyData.maxTimeToFinish ?? 600;
        m.maxTimeToFinishPage =
            surveyData.maxTimeToFinishPage ??
            surveyData.maxTimeToFinish ??
            600;
        m.completeText = 'Completa';
        return m;
    }, [surveyData, alertResults]);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
        @keyframes pulse {
            0% { transform: scale(1.2); }
            50% { transform: scale(1.5); }
            100% { transform: scale(1.2); }
        }

        .quiz-results-detail.table-responsive {
            overflow-x: auto;
        }

        .quiz-results-detail .table td,
        .quiz-results-detail .table th {
            white-space: normal !important;
            word-wrap: break-word;
            vertical-align: top;
        }

        .quiz-results-detail .table th:last-child,
        .quiz-results-detail .table td:last-child {
            min-width: 12rem;
        }
    `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const ResultsView = () => {
        if (!quizResults) return null;

        return (
            <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
                <div className="container py-5">
                    <div className="text-center mb-5">
                        <h2 className="display-4 mb-3">
                            {quizResults.summary.percentage >= QUIZ_PASS_THRESHOLD_PERCENT ? 'Ottimo lavoro!' : 'Continua a studiare!'}
                        </h2>
                        <p className="lead text-muted">
                            {quizResults.summary.percentage >= QUIZ_PASS_THRESHOLD_PERCENT
                                ? 'Hai superato il test con successo!' 
                                : 'Non mollare, puoi fare di meglio!'}
                        </p>
                    </div>

                    {/* Circular Progress */}
                    <div className="row justify-content-center mb-5">
                        <div className="col-md-4 text-center">
                            <div className="position-relative d-inline-block">
                                <svg width="200" height="200" viewBox="0 0 200 200">
                                    <circle
                                        cx="100"
                                        cy="100"
                                        r="90"
                                        fill="none"
                                        stroke="#e9ecef"
                                        strokeWidth="12"
                                    />
                                    <circle
                                        cx="100"
                                        cy="100"
                                        r="90"
                                        fill="none"
                                        stroke={quizResults.summary.percentage >= QUIZ_PASS_THRESHOLD_PERCENT ? '#28a745' : '#dc3545'}
                                        strokeWidth="12"
                                        strokeDasharray={`${2 * Math.PI * 90}`}
                                        strokeDashoffset={`${2 * Math.PI * 90 * (1 - quizResults.summary.percentage / 100)}`}
                                        style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
                                        transform="rotate(-90 100 100)"
                                    />
                                </svg>
                                <div 
                                    className="position-absolute" 
                                    style={{ 
                                        top: '50%', 
                                        left: '50%', 
                                        transform: 'translate(-50%, -50%)' 
                                    }}
                                >
                                    <h1 className={`display-4 mb-0 ${
                                        quizResults.summary.percentage >= QUIZ_PASS_THRESHOLD_PERCENT ? 'text-success' : 'text-danger'
                                    }`}>
                                        {quizResults.summary.percentage}%
                                    </h1>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="row justify-content-center mb-5">
                        <div className="col-md-8">
                            <div className="row g-4">
                                <div className="col-md-4">
                                    <div className="p-4 rounded-4 bg-white text-center h-100" style={{ boxShadow: '0 0.125rem 0.25rem rgba(0,0,0,0.075)' }}>
                                        <i className="bi bi-trophy-fill text-warning display-5 mb-3"></i>
                                        <h5>Punteggio</h5>
                                        <h3 className="text-primary mb-0">{quizResults.summary.score}/{quizResults.summary.maxScore}</h3>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="p-4 rounded-4 bg-white text-center h-100" style={{ boxShadow: '0 0.125rem 0.25rem rgba(0,0,0,0.075)' }}>
                                        <i className="bi bi-check2-circle text-success display-5 mb-3"></i>
                                        <h5>Risposte Corrette</h5>
                                        <h3 className="text-success mb-0">{quizResults.summary.correctAnswers}/{quizResults.summary.totalQuestions}</h3>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="p-4 rounded-4 bg-white text-center h-100" style={{ boxShadow: '0 0.125rem 0.25rem rgba(0,0,0,0.075)' }}>
                                        <i className="bi bi-clock-fill text-info display-5 mb-3"></i>
                                        <h5>Tempo</h5>
                                        <h3 className="text-info mb-0">{quizResults.summary.timeSpent} min</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Answers Table */}
                    <div className="row justify-content-center">
                        <div className="col-lg-10 col-xl-9">
                            <div className="bg-white rounded-4 p-5" style={{ boxShadow: '0 0.125rem 0.25rem rgba(0,0,0,0.075)' }}>
                                <h4 className="mb-4">Dettaglio Risposte</h4>
                                <div className="table-responsive quiz-results-detail">
                                    <table className="table table-borderless mb-0 align-middle">
                                        <thead>
                                            <tr className="border-bottom">
                                                <th className="text-muted small text-uppercase">Domanda</th>
                                                <th className="text-muted small text-uppercase">Tua risposta</th>
                                                <th className="text-muted small text-uppercase">Argomento da ripassare</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {quizResults.details.map((result, index) => (
                                                <tr key={index} className="border-bottom">
                                                    <td className="fw-medium">{result.title}</td>
                                                    <td>
                                                        <div className="d-flex align-items-start">
                                                            <i
                                                                className={`bi mt-1 ${
                                                                    result.isCorrect
                                                                        ? 'bi-check-circle-fill text-success'
                                                                        : 'bi-x-circle-fill text-danger'
                                                                } me-2 flex-shrink-0`}
                                                            />
                                                            <span>
                                                                {result.userAnswerLabel ?? result.userAnswer}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {!result.isCorrect && result.ripasso ? (
                                                            <span className="text-primary">
                                                                <i className="bi bi-bookmark-star me-1" />
                                                                {result.ripasso}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="d-flex justify-content-center gap-3 mt-5">
                                <Button 
                                    variant="primary" 
                                    size="lg"
                                    onClick={() => window.location.reload()}
                                >
                                    <i className="bi bi-arrow-repeat me-2"></i>
                                    Riprova
                                </Button>
                                <Button 
                                    variant="outline-dark" 
                                    size="lg"
                                    onClick={() => {
                                        if (classeData) {
                                            navigate('/area-personale/corso', { state: { classe: classeData } });
                                        } else {
                                            navigate(-1);
                                        }
                                    }}
                                >
                                    <i className="bi bi-arrow-left me-2"></i>
                                    Torna al Corso
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const CountdownView = () => (
        <div style={{ 
            backgroundColor: '#f8f9fa', 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
        }}>
            <div className="text-center">
                <h3 className="mb-4 text-muted">
                    Il test sta per iniziare...
                </h3>
                <div 
                    className="display-1 fw-bold"
                    style={{ 
                        color: countdown === 1 ? 'orange' : 
                               countdown === 2 ? 'orange' : 'orange',
                        transition: 'all 0.3s ease-in-out',
                        transform: 'scale(1.2)',
                        animation: 'pulse 1s infinite'
                    }}
                >
                    {countdown}
                </div>
                <p className="mt-3 text-muted">
                    Preparati a rispondere alle domande
                </p>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div style={{ 
                backgroundColor: '#f8f9fa', 
                minHeight: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
            }}>
                <div className="text-center">
                    <Spinner 
                        animation="border" 
                        role="status"
                        style={{ width: '3rem', height: '3rem' }}
                        className="text-primary mb-3"
                    >
                        <span className="visually-hidden">Caricamento test...</span>
                    </Spinner>
                    <h5 className="text-muted">Caricamento test...</h5>
                </div>
            </div>
        );
    }

    if (!surveyData) {
        return (
            <div className="text-center p-5">
                <h4>Nessun test disponibile per questo contenuto</h4>
                <Button className='mt-3' variant="primary" onClick={() => navigate(-1)}>Torna al corso</Button>
            </div>
        );
    }

    return (
        <>
            {!showQuiz ? (
                <CountdownView />
            ) : !showResults ? (
                survey ? <Survey model={survey} /> : null
            ) : (
                <ResultsView />
            )}
        </>
    );
}

export default QuizExecutionPage;

